import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { customAxiosInstanceMutator } from '../../../services/custom-axios-instance';
import CreditDirectorReportsModal from '../loan-processing/CreditDirectorReportsModal';
import { DirectorReportView } from '../loan-processing/creditDirectorReportsTypes';

interface CreditCheckResponse {
  directorIdNumber: string;
  directorName: string;
  enquiryId?: string;
  enquiryResultId?: string;
  report?: Record<string, unknown>;
  scoringEngineResult?: Record<string, unknown>;
  attributes?: Record<string, unknown>;
  failureReason?: string | null;
  recommendation?: string;
  riskLevel?: string;
}

const CreditAssessmentStandalone = () => {
  const [idNumber, setIdNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CreditCheckResponse | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);

  const handleRunCreditCheck = async () => {
    if (!idNumber.trim() || idNumber.trim().length < 10) {
      toast.error('Please enter a valid ID number');
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const response = await customAxiosInstanceMutator<CreditCheckResponse>({
        url: '/loan/api/v1/credit-report',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { idNumber: idNumber.trim(), existingCustomer: false }
      });

      setResult(response);
      toast.success('Credit check completed successfully');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to run credit check';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const getReportViews = (): DirectorReportView[] => {
    if (!result) return [];

    return [{
      id: result.directorIdNumber,
      label: result.directorName || result.directorIdNumber,
      status: result.failureReason ? 'FAILED' : 'SUCCESS',
      creditScore: (result.report as any)?.consumerScoring?.finalScore ?? null,
      xdsReport: result.report,
      attributes: result.attributes,
      scoringEngineResult: result.scoringEngineResult
    }];
  };

  const getModelProbability = (): number | null => {
    const scoring = (result?.scoringEngineResult as any)?.scoring;
    return scoring?.model_probability ?? null;
  };

  const getRiskBand = (): string => {
    const scoring = (result?.scoringEngineResult as any)?.scoring;
    return scoring?.risk_band || result?.riskLevel || 'N/A';
  };

  return (
    <div>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Credit Assessment</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-8">
              <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                <div className="card-header bg-transparent border-0 pb-0 pt-4 px-4">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#e7f3ff', border: '1px solid rgba(13,110,253,0.2)' }}>
                      <i className="fa fa-search fa-lg text-primary" style={{ display: 'block' }}></i>
                    </div>
                    <div>
                      <h4 className="mb-0 fw-bold">Quick Credit Assessment</h4>
                      <p className="text-muted mb-0 small">Run a credit check for an individual by ID number</p>
                    </div>
                  </div>
                </div>
                <div className="card-body p-4">
                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-8">
                      <label className="form-label fw-semibold">ID Number</label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Enter 13-digit SA ID number"
                        value={idNumber}
                        onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, '').slice(0, 13))}
                        maxLength={13}
                        disabled={isLoading}
                      />
                      <small className="text-muted">{idNumber.length}/13 digits</small>
                    </div>
                    <div className="col-12 col-md-4 pb-4">
                      <button
                        className="btn btn-primary btn-lg w-100"
                        onClick={handleRunCreditCheck}
                        disabled={isLoading || idNumber.length < 13}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Running...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-search me-2"></i>
                            Run Credit Check
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Result Card */}
              {result && (
                <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: '12px' }}>
                  <div className="card-header bg-transparent border-0 pt-4 px-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <h5 className="mb-0 fw-bold">
                        <i className="fa fa-check-circle text-success me-2"></i>
                        Credit Check Result
                      </h5>
                      <button
                        className="btn btn-outline-primary btn-sm rounded-pill px-3"
                        onClick={() => setShowReportModal(true)}
                      >
                        <i className="fa fa-file-text me-2"></i>
                        View Full Report
                      </button>
                    </div>
                  </div>
                  <div className="card-body p-4">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted d-block mb-1">Full Name</small>
                          <span className="fw-bold fs-5">{result.directorName || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted d-block mb-1">ID Number</small>
                          <span className="fw-bold fs-5">{result.directorIdNumber}</span>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted d-block mb-1">Recommendation</small>
                          <span className={`badge px-3 py-2 fs-6 ${
                            result.recommendation === 'APPROVE' || result.recommendation === 'APPROVE_WITH_CONDITIONS' ? 'bg-success' :
                            result.recommendation === 'REJECT' ? 'bg-danger' : 'bg-warning text-dark'
                          }`}>
                            {result.recommendation || 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted d-block mb-1">Risk Band</small>
                          <span className="fw-bold">{getRiskBand()}</span>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="p-3 bg-light rounded-3">
                          <small className="text-muted d-block mb-1">Model Probability</small>
                          <span className="fw-bold">{getModelProbability() != null ? `${getModelProbability()}%` : 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      <CreditDirectorReportsModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        reports={getReportViews()}
        selectedIndex={0}
        onSelectIndex={() => {}}
        processedCount={result ? 1 : 0}
        successCount={result ? 1 : 0}
        failedCount={0}
        showDownloadControl={true}
        downloadEnabled={true}
      />
    </div>
  );
};

export default CreditAssessmentStandalone;
